/**
 * Hook pour WebGL Pattern Renderer avec fallback Canvas 2D
 *
 * Utilise GPU si disponible, sinon Canvas 2D
 * PERF: grayRamp 100x+ plus rapide en GPU
 */

import { useEffect, useRef, useCallback } from 'react';
import { WebGLPatternRenderer } from '@/components/webgl-pattern-renderer';

export function useWebGLRenderer(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  powerPreference: 'low-power' | 'high-performance' | 'default' = 'default'
) {
  const webglRef = useRef<WebGLPatternRenderer | null>(null);
  const useWebGLRef = useRef<boolean>(false);
  const currentPowerPreferenceRef = useRef<string>(powerPreference);

  // Réinitialiser WebGL si powerPreference change
  useEffect(() => {
    currentPowerPreferenceRef.current = powerPreference;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Nettoyer le renderer précédent
    if (webglRef.current) {
      webglRef.current.destroy();
      webglRef.current = null;
    }

    try {
      webglRef.current = new WebGLPatternRenderer(
        canvas.width,
        canvas.height,
        powerPreference
      );
      useWebGLRef.current = true;
      // console.log(`[WebGL] Renderer initialized with powerPreference='${powerPreference}'`);
    } catch (error) {
      // ('[WebGL] Not available, falling back to Canvas 2D:', error);
      useWebGLRef.current = false;
      webglRef.current = null;
    }

    return () => {
      if (webglRef.current) {
        webglRef.current.destroy();
        webglRef.current = null;
      }
    };
  }, [canvasRef, powerPreference]);

  /**
   * Render pattern - utilise GPU si disponible, sinon CPU
   */
  const renderPattern = (
    patternType: string,
    drawFallback?: () => ImageData
  ): ImageData | null => {
    // Si WebGL disponible et pattern supporté
    const supportedPatterns = [
      'grayRamp',
      'redRamp',
      'greenRamp',
      'blueRamp',
      'whiteRamp',
      'colorBar',
      'grayBar8',
      'grayBar16',
      'multiburst',
      'gamma22'
    ];

    if (useWebGLRef.current && webglRef.current && supportedPatterns.includes(patternType)) {
      try {
        return webglRef.current.render(patternType);
      } catch (error) {
        // ('[WebGL] Render failed, using fallback:', error);
      }
    }

    // Fallback: utiliser Canvas 2D
    if (drawFallback) {
      return drawFallback();
    }

    return null;
  };

  return {
    isWebGLAvailable: useWebGLRef.current,
    renderPattern,
    webglRenderer: webglRef.current
  };
}
